import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import { ProtectedRoute } from '@/components/shared/ProtectedRoute'
import { PublicOnlyRoute } from '@/components/shared/PublicOnlyRoute'
import { Skeleton } from '@/components/ui/skeleton'

// Eagerly load the auth page — the first thing logged-out visitors see.
// The marketing landing lives on the separate site (lumatara.com); the app
// subdomains (app./dev.lumatara.com) go straight into the product.
import AuthPage from '@/pages/AuthPage'

// All authenticated pages are lazy-loaded to keep the initial bundle small.
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const FeedPage = lazy(() => import('@/pages/FeedPage'))
const FeedArticlePage = lazy(() => import('@/pages/FeedArticlePage'))
const InvestmentsPage = lazy(() => import('@/pages/InvestmentsPage'))
const PortfolioPage = lazy(() => import('@/pages/PortfolioPage'))
const NetWorthPage = lazy(() => import('@/pages/NetWorthPage'))
const InvestmentsHoldingsPage = lazy(() => import('@/pages/InvestmentsHoldingsPage'))
const InvestmentsHoldingDetailPage = lazy(() => import('@/pages/InvestmentsHoldingDetailPage'))
const InvestmentsTransactionsPage = lazy(() => import('@/pages/InvestmentsTransactionsPage'))
const IntegrationsPage = lazy(() => import('@/pages/IntegrationsPage'))
const ZerodhaIntegrationPage = lazy(() => import('@/pages/integrations/ZerodhaIntegrationPage'))
const ZerodhaCallbackPage = lazy(() => import('@/pages/integrations/ZerodhaCallbackPage'))
const PendingMatchesPage = lazy(() => import('@/pages/integrations/PendingMatchesPage'))
const CsvImportPage = lazy(() => import('@/pages/integrations/CsvImportPage'))
const ImportHistoryPage = lazy(() => import('@/pages/integrations/ImportHistoryPage'))
const BookmarksPage = lazy(() => import('@/pages/BookmarksPage'))
const TasksPage = lazy(() => import('@/pages/TasksPage'))
const NotesPage = lazy(() => import('@/pages/NotesPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const StyleGuidePage = lazy(() => import('@/pages/StyleGuidePage'))
const ExtensionPage = lazy(() => import('@/pages/ExtensionPage'))
const ToolsPage = lazy(() => import('@/pages/tools/ToolsPage'))
const AudiobookToolPage = lazy(() => import('@/pages/tools/AudiobookToolPage'))
const FinancialCalculatorToolPage = lazy(() => import('@/pages/tools/FinancialCalculatorToolPage'))
const CurrencyConverterToolPage = lazy(() => import('@/pages/tools/CurrencyConverterToolPage'))
const UnitConverterToolPage = lazy(() => import('@/pages/tools/UnitConverterToolPage'))
const WorldClockToolPage = lazy(() => import('@/pages/tools/WorldClockToolPage'))
const PomodoroToolPage = lazy(() => import('@/pages/tools/PomodoroToolPage'))

const queryClient = new QueryClient()

function PageSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-3 p-6">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* The app subdomains have no marketing landing — go straight
                  into the product. ProtectedRoute bounces logged-out visitors
                  to /auth and lets authenticated users through to /dashboard. */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/auth"
                element={
                  <PublicOnlyRoute>
                    <AuthPage />
                  </PublicOnlyRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <ProtectedRoute>
                    <FeedPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feed/articles/:id"
                element={
                  <ProtectedRoute>
                    <FeedArticlePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments"
                element={
                  <ProtectedRoute>
                    <InvestmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portfolio"
                element={
                  <ProtectedRoute>
                    <PortfolioPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/net-worth"
                element={
                  <ProtectedRoute>
                    <NetWorthPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/holdings"
                element={
                  <ProtectedRoute>
                    <InvestmentsHoldingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/holdings/:instrumentId"
                element={
                  <ProtectedRoute>
                    <InvestmentsHoldingDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/transactions"
                element={
                  <ProtectedRoute>
                    <InvestmentsTransactionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/import"
                element={<Navigate to="/integrations" replace />}
              />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/zerodha"
                element={
                  <ProtectedRoute>
                    <ZerodhaIntegrationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/zerodha/callback"
                element={
                  <ProtectedRoute>
                    <ZerodhaCallbackPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/csv-import"
                element={
                  <ProtectedRoute>
                    <CsvImportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/import-history"
                element={
                  <ProtectedRoute>
                    <ImportHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/integrations/vested" element={<Navigate to="/integrations/csv-import" replace />} />
              <Route path="/integrations/cams-kfintech" element={<Navigate to="/integrations/csv-import" replace />} />
              <Route path="/integrations/epf" element={<Navigate to="/integrations/csv-import" replace />} />
              <Route path="/integrations/ppf" element={<Navigate to="/integrations/csv-import" replace />} />
              <Route
                path="/integrations/pending-matches"
                element={
                  <ProtectedRoute>
                    <PendingMatchesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookmarks"
                element={
                  <ProtectedRoute>
                    <BookmarksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <TasksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notes"
                element={
                  <ProtectedRoute>
                    <NotesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools"
                element={
                  <ProtectedRoute>
                    <ToolsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/audiobook"
                element={
                  <ProtectedRoute>
                    <AudiobookToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/financial-calculator"
                element={
                  <ProtectedRoute>
                    <FinancialCalculatorToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/currency-converter"
                element={
                  <ProtectedRoute>
                    <CurrencyConverterToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/unit-converter"
                element={
                  <ProtectedRoute>
                    <UnitConverterToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/world-clock"
                element={
                  <ProtectedRoute>
                    <WorldClockToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tools/pomodoro"
                element={
                  <ProtectedRoute>
                    <PomodoroToolPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/extension"
                element={
                  <ProtectedRoute>
                    <ExtensionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/internal/style-guide"
                element={
                  <ProtectedRoute>
                    <StyleGuidePage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
          <Toaster position="bottom-right" richColors />
          <Analytics />
          <SpeedInsights />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  )
}
